import { ProviderDetails } from '../../redux/providerBuilder/providerDetails/types';
import {
  EDIT_PROVIDER,
  GET_PROVIDER_BY_ID,
  GET_PROVIDER_NETWORK_REQUEST_DATA,
  REGISTER_PROVIDER,
  SAVE_PROVIDER_NETWORK_REQUEST_DATA,
} from '../constants';

/**
 * Get the real regex string from the regex string (.*? or .*)
 * @param regex
 * @param matchType
 * @returns '.*?' or '.*'
 */
function getRealRegexString(
  regex: string,
  matchType: string,
  oprfEnabled: boolean = false,
  variableName: string = ''
) {
  return regex.replace(/{{(.*?)}}/g, (_match: any) => {
    if (oprfEnabled && variableName) {
      return matchType === 'lazy' ? `(?<${variableName}>.*?)` : `(?<${variableName}>.*)`;
    }
    return matchType === 'lazy' ? `(.*?)` : `(.*)`;
  });
}

// function getRealRegexString(regex: string, matchType: string) {
//   const escapedRegex = regex.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
//   const tempReplaced = escapedRegex.replace(/\{\{([^}]*)\}\}/g, "<<$1>>")
//   const finalRegex = tempReplaced.replace(/<<([^>]*)>>/g, matchType === "lazy" ? "(.*?)" : "(.*)")

//   return finalRegex
// }

/**
 * Get the match type from the regex string (lazy or greedy)
 * @param regex
 * @returns 'lazy' or 'greedy'
 */
function getMatchTypeFromRegex(regex: string): string {
  return regex.includes('.*?') ? 'lazy' : 'greedy';
}

/**
 * Transforms an HttpProvider object into the new schema with requestData.
 *
 * @param provider The HttpProvider object to transform
 * @returns The transformed provider object
 */
function transformHttpProvider(provider: any, selectedNetworkRequest: any): any {
  // const responseMatches = provider.responseSelections.map((selection: any) => ({
  //   value: selection.responseMatch,
  //   type: "contains",
  //   invert: selection.invert ?? false,
  // }))

  // const responseRedactions = provider.responseSelections.map((selection: any) => ({
  //   xPath: selection.xPath ?? "",
  //   jsonPath: selection.jsonPath ?? "",
  //   regex: getRealRegexString(selection.responseMatch, selection.matchType),
  //   hash: selection.hash ?? "",
  // }))

  return {
    ...provider, // Keep other properties
    requestData: [
      ...new Map(
        provider.responseSelections.map((selection: any) => [
          selection.requestIndex,
          {
            url:
              selectedNetworkRequest?.[selection.requestIndex]?.templateUrl ??
              selection.url,
            urlType: provider.urlType ?? 'TEMPLATE',
            method: selection.method,
            responseMatches: provider.responseSelections
              .filter((item: any) => item.requestIndex === selection.requestIndex)
              .map((item: any, index: number) => ({
                value: item.responseMatch,
                type: 'contains',
                invert: item.invert ?? false,
                order: index,
              })),
            responseRedactions: provider.responseSelections
              .filter((item: any) => item.requestIndex === selection.requestIndex)
              .map((item: any) => ({
                xPath: item.xPath ?? '',
                jsonPath: item.jsonPath ?? '',
                regex: getRealRegexString(
                  item.responseMatch,
                  item.matchType,
                  item?.hash === 'oprf',
                  item?.variableName
                ),
                hash: item?.hash ?? '',
              })),
            bodySniff: selectedNetworkRequest?.[selection.requestIndex]?.bodySniff ?? {
              enabled: false,
              regex: '',
              template: '',
            },
          },
        ])
      ).values(),
    ],
  };
}

async function registerProvider(
  providerDetails: ProviderDetails,
  selectedNetworkRequest: any,
  accessToken: string
): Promise<any> {
  if (!accessToken) {
    return { isSuccess: false, message: 'Access token is required' };
  }

  const transformedProvider = transformHttpProvider(
    providerDetails,
    selectedNetworkRequest
  );
  console.log({ transformedProvider });

  try {
    const data = await fetch(REGISTER_PROVIDER, {
      headers: {
        'authorization': `Bearer ${accessToken}`,
        'content-type': 'application/json',
      },

      body: JSON.stringify(transformedProvider),
      method: 'POST',
    });
    const message = await data.json();
    return message;
  } catch (e) {
    return { isSuccess: false, message: 'Error while registering provider' };
  }
}

async function makeProviderActive(accessToken: string, providerId: string): Promise<any> {
  if (!accessToken) {
    return { isSuccess: false, message: 'Access token is required' };
  }

  try {
    const data = await fetch(`${GET_PROVIDER_BY_ID(providerId)}`, {
      headers: {
        'authorization': `Bearer ${accessToken}`,
        'content-type': 'application/json',
      },

      body: JSON.stringify({ isActive: true }),
      method: 'POST',
    });
    const message = await data.json();
    return message;
  } catch (e) {
    return { isSuccess: false, message: 'Error while updating provider' };
  }
}

async function getProviderById(accessToken: string, providerId: string): Promise<any> {
  if (!accessToken) {
    return { isSuccess: false, message: 'Access token is required' };
  }

  try {
    const data = await fetch(`${GET_PROVIDER_BY_ID(providerId)}`, {
      headers: {
        'authorization': `Bearer ${accessToken}`,
        'content-type': 'application/json',
      },
      method: 'GET',
    });
    const message = await data.json();
    return message.providers;
  } catch (e) {
    return { isSuccess: false, message: 'Error while fetching provider' };
  }
}

async function editProvider({
  providerDetails,
  selectedNetworkRequest,
  accessToken,
  skipTransform,
}: {
  providerDetails: ProviderDetails;
  selectedNetworkRequest: any;
  accessToken: string;
  skipTransform?: boolean;
}): Promise<any> {
  if (!accessToken) {
    return { isSuccess: false, message: 'Access token is required' };
  }

  let transformedProvider = providerDetails;

  if (!skipTransform) {
    transformedProvider = transformHttpProvider(providerDetails, selectedNetworkRequest);
  }

  try {
    const data = await fetch(EDIT_PROVIDER(providerDetails.httpProviderId), {
      headers: {
        'authorization': `Bearer ${accessToken}`,
        'content-type': 'application/json',
      },

      body: JSON.stringify(transformedProvider),
      method: 'POST',
    });
    const message = await data.json();
    return message;
  } catch (e) {
    return { isSuccess: false, message: 'Error while editing provider' };
  }
}

async function saveProviderNetworkRequest(
  networkRequestDetails: any,
  accessToken: string
): Promise<any> {
  if (!accessToken) {
    return { isSuccess: false, message: 'Access token is required' };
  }

  try {
    const data = await fetch(
      SAVE_PROVIDER_NETWORK_REQUEST_DATA(networkRequestDetails.httpProviderId),
      {
        headers: {
          'authorization': `Bearer ${accessToken}`,
          'content-type': 'application/json',
        },

        body: JSON.stringify(networkRequestDetails),
        method: 'POST',
      }
    );
    const message = await data.json();
    return message;
  } catch (e) {
    console.error('something went wrong wile saving provider network request');
    return {
      isSuccess: false,
      message: 'Error while saving provider network details',
    };
  }
}

async function getProviderNetworkRequest(
  accessToken: string,
  providerId: string
): Promise<any> {
  if (!accessToken) {
    return { isSuccess: false, message: 'Access token is required' };
  }

  try {
    const data = await fetch(`${GET_PROVIDER_NETWORK_REQUEST_DATA(providerId)}`, {
      headers: {
        'authorization': `Bearer ${accessToken}`,
        'content-type': 'application/json',
      },
      method: 'GET',
    });
    const message = await data.json();
    return message.data;
  } catch (e) {
    return { isSuccess: false, message: 'Error while fetching provider' };
  }
}

export {
  registerProvider,
  makeProviderActive,
  getProviderById,
  editProvider,
  saveProviderNetworkRequest,
  getProviderNetworkRequest,
};
